import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Text } from '@fluentui/react-components';
import IPoll from '../../model/IPoll';
import { useDataService } from '../../app/DataServiceContext';
import { useAppToast } from '../../app/ToastContext';
import { buildDuplicateRequest } from '../../lib/pollQuestions';
import { copyText } from '../../lib/clipboard';
import ConfirmDialog from '../ui/ConfirmDialog';

export interface IPollActions {
    /** Opens the confirmation dialog. */
    requestDelete: (poll: IPoll) => void;
    duplicate: (poll: IPoll) => Promise<void>;
    copyLink: (poll: IPoll) => Promise<void>;
    openSurvey: (poll: IPoll) => void;
    surveyUrl: (poll: IPoll) => string;
    duplicating: boolean;
    /** Render once per page. */
    dialog: React.ReactNode;
}

export interface IPollActionsOptions {
    /** Called after a successful delete (e.g. reload the list or leave the page). */
    onDeleted?: (poll: IPoll) => void;
}

/** Delete / duplicate / share actions shared by the list and the details page. */
export function usePollActions(options: IPollActionsOptions = {}): IPollActions {
    const dataService = useDataService();
    const toast = useAppToast();
    const navigate = useNavigate();
    const [pollToDelete, setPollToDelete] = React.useState<IPoll>();
    const [deleting, setDeleting] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState<string>();
    const [duplicating, setDuplicating] = React.useState(false);
    const onDeletedRef = React.useRef(options.onDeleted);
    onDeletedRef.current = options.onDeleted;

    const surveyUrl = React.useCallback((poll: IPoll) => dataService.getSurveyUrl(poll.Slug), [dataService]);

    const requestDelete = React.useCallback((poll: IPoll) => {
        setDeleteError(undefined);
        setPollToDelete(poll);
    }, []);

    const executeDelete = async (): Promise<void> => {
        if (!pollToDelete?.Id) return;
        try {
            setDeleting(true);
            setDeleteError(undefined);
            await dataService.deletePoll(pollToDelete.Id);
            const deleted = pollToDelete;
            setPollToDelete(undefined);
            toast.success('Опрос удалён', `«${deleted.Title ?? ''}»`);
            onDeletedRef.current?.(deleted);
        } catch (e) {
            setDeleteError((e as Error)?.message ?? 'Не удалось удалить опрос. Попробуйте снова.');
        } finally {
            setDeleting(false);
        }
    };

    const duplicate = React.useCallback(async (poll: IPoll): Promise<void> => {
        if (!poll.Id || duplicating) return;
        try {
            setDuplicating(true);
            const full = await dataService.getPoll(poll.Id);
            const created = await dataService.createPoll(buildDuplicateRequest({ ...poll, ...full }));
            toast.success('Копия создана', created?.Title ?? undefined);
            navigate(created?.Id ? `/polls/${encodeURIComponent(created.Id)}` : '/polls');
        } catch (e) {
            toast.error('Не удалось дублировать опрос', (e as Error)?.message);
        } finally {
            setDuplicating(false);
        }
    }, [dataService, duplicating, navigate, toast]);

    const copyLink = React.useCallback(async (poll: IPoll): Promise<void> => {
        const url = surveyUrl(poll);
        if (!url) {
            toast.error('У опроса нет ссылки', 'Slug ещё не назначен.');
            return;
        }
        const ok = await copyText(url);
        if (ok) toast.success('Ссылка скопирована');
        else toast.error('Не удалось скопировать ссылку');
    }, [surveyUrl, toast]);

    const openSurvey = React.useCallback((poll: IPoll): void => {
        const url = surveyUrl(poll);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
    }, [surveyUrl]);

    const dialog = (
        <ConfirmDialog
            open={!!pollToDelete}
            title="Удалить опрос?"
            confirmLabel="Удалить"
            busyLabel="Удаление"
            busy={deleting}
            error={deleteError}
            danger
            onCancel={() => { if (!deleting) setPollToDelete(undefined); }}
            onConfirm={() => { void executeDelete(); }}
        >
            <Text>
                Опрос <strong>«{pollToDelete?.Title}»</strong> и все его ответы будут удалены. Это действие нельзя отменить.
            </Text>
        </ConfirmDialog>
    );

    return { requestDelete, duplicate, copyLink, openSurvey, surveyUrl, duplicating, dialog };
}
